// BHP-1038: single source of truth for the inline SVG icons used by the shell.
// Consolidated here (instead of scattered inline SVGs) so the icon set is
// reusable, de-duplicated and swappable in one place.

interface IconProps {
  className?: string;
}

const PATHS = {
  home: 'M3 11.5l9-8 9 8M5 10v10h5v-6h4v6h5V10',
  monitor: 'M4 5h16v10H4zM9 20h6M12 15v5',
  tag: 'M11 3H4v7l9 9 7-7-9-9zM7.5 7.5h.01',
  envelope: 'M3 5h18v14H3zM3 6l9 7 9-7',
  grid: 'M4 4h6v6H4zM14 4h6v6h-6zM14 14h6v6h-6zM4 14h6v6H4z',
  basket:
    'M5 8h14l-1.2 11.5a1 1 0 01-1 .9H7.2a1 1 0 01-1-.9L5 8zM9 8V6a3 3 0 016 0v2',
  arrowLeft: 'M19 12H5M12 19l-7-7 7-7',
  arrowRight: 'M5 12h14M12 5l7 7-7 7',
  chevronDown: 'M6 9l6 6 6-6',
  account: 'M12 12a4 4 0 100-8 4 4 0 000 8zM4 20a8 8 0 0116 0',
  logout: 'M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9',
  dot: 'M12 12h.01'
} as const;

function Svg({ d, className }: { d: string; className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

export function HomeIcon({ className }: IconProps): JSX.Element {
  return <Svg d={PATHS.home} className={className} />;
}

export function ChevronDownIcon({ className }: IconProps): JSX.Element {
  return <Svg d={PATHS.chevronDown} className={className} />;
}

export function AccountIcon({ className }: IconProps): JSX.Element {
  return <Svg d={PATHS.account} className={className} />;
}

export function LogoutIcon({ className }: IconProps): JSX.Element {
  return <Svg d={PATHS.logout} className={className} />;
}

export function ArrowIcon({
  direction = 'left',
  className
}: IconProps & { direction?: 'left' | 'right' }): JSX.Element {
  return (
    <Svg
      d={direction === 'left' ? PATHS.arrowLeft : PATHS.arrowRight}
      className={className}
    />
  );
}

// Maps the legacy NavigationService icon-font keys to inline SVGs (the
// simple-line-icons font renders as broken glyphs in the new shell).
const NAV_KEYS: Record<string, keyof typeof PATHS> = {
  'icon-home': 'home',
  'icon-screen-desktop': 'monitor',
  'icon-tag': 'tag',
  'icon-envelope': 'envelope',
  'icon-grid': 'grid',
  'icon-basket': 'basket'
};

export function NavIcon({
  name,
  className
}: {
  name: string;
  className?: string;
}): JSX.Element {
  return <Svg d={PATHS[NAV_KEYS[name] ?? 'dot']} className={className} />;
}
